import type { TFunction } from 'i18next';
import api from '../../services/api';
import { mapOf, toTranslations } from '../MenuItems/utils';
import type { ItemRecord } from '../MenuItems/types';
import type { EditorCategory, EditorState, MenuRecord } from './types';

export const newCategory = (languages: string[]): EditorCategory => ({
    key: `new-${crypto.randomUUID()}`,
    id: null,
    names: Object.fromEntries(languages.map((l) => [l, ''])),
    itemIds: [],
});

/** Editor state from the saved menu. Items that are not in the catalogue (archived) are dropped. */
export const toEditorState = (menu: MenuRecord | null, languages: string[], catalogue: ItemRecord[]): EditorState => {
    const blank = Object.fromEntries(languages.map((l) => [l, '']));
    if (!menu) return { isActive: true, names: blank, descriptions: { ...blank }, categories: [] };
    const known = new Set(catalogue.map((i) => i.id));
    const sorted = [...(menu.categories ?? [])].sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
    return {
        isActive: menu.isActive,
        names: { ...blank, ...mapOf(menu.translations, 'name') },
        descriptions: { ...blank, ...mapOf(menu.translations, 'description') },
        categories: sorted.map((c) => ({
            key: c.id,
            id: c.id,
            names: { ...blank, ...Object.fromEntries(c.translations.map((tr) => [tr.language, tr.name])) },
            itemIds: (c.menuItems ?? []).map((i) => i.id).filter((id) => known.has(id)),
        })),
    };
};

export const nameKey = (language: string) => `name.${language}`;
export const categoryKey = (key: string, language: string) => `category.${key}.${language}`;

/** Every supported language needs a menu name and a name for every category. Returns only the failing fields. */
export const validateMenu = (state: EditorState, languages: string[], t: TFunction<'menu'>): Record<string, string> => {
    const errors: Record<string, string> = {};
    for (const lang of languages) {
        if (!state.names[lang]?.trim()) errors[nameKey(lang)] = t('editor.errors.name');
        for (const c of state.categories) {
            if (!c.names[lang]?.trim()) errors[categoryKey(c.key, lang)] = t('editor.errors.categoryName');
        }
    }
    return errors;
};

/** What is already on the server, updated as the save progresses so a retry continues instead of duplicating. */
export interface SaveProgress {
    menuId: string | null;
    /** Ids of the categories attached to the menu. */
    attached: Set<string>;
}

export interface SaveCallbacks {
    menuCreated: (menuId: string) => void;
    categoryAttached: (key: string, id: string) => void;
    categoryDetached: (id: string) => void;
}

/**
 * Saves the menu, then its categories in order, as the API contract describes:
 * POST /categories {restaurantId, translations, order}, POST /menus/:id/categories {categoryId, menuItemIds},
 * PUT /categories/:id {translations, order}, PUT /menus/:id/categories/:categoryId {menuItemIds}.
 */
export const saveMenu = async (restaurantId: string, state: EditorState, progress: SaveProgress, cb: SaveCallbacks): Promise<string> => {
    const body = { isActive: state.isActive, translations: toTranslations(state.names, state.descriptions) };
    let menuId = progress.menuId;
    if (menuId) {
        await api.put(`/menus/${menuId}`, body);
    } else {
        const { data } = await api.post<{ id: string }>('/menus', { restaurantId, ...body });
        menuId = data.id;
        cb.menuCreated(menuId);
    }

    // Decided before the loop: categories attached below must not count as removed.
    const removed = [...progress.attached].filter((id) => !state.categories.some((c) => c.id === id));

    for (const [order, category] of state.categories.entries()) {
        const translations = toTranslations(category.names);
        if (category.id && progress.attached.has(category.id)) {
            await api.put(`/categories/${category.id}`, { translations, order });
            await api.put(`/menus/${menuId}/categories/${category.id}`, { menuItemIds: category.itemIds });
            continue;
        }
        const { data } = await api.post<{ id: string }>('/categories', { restaurantId, translations, order });
        try {
            await api.post(`/menus/${menuId}/categories`, { categoryId: data.id, menuItemIds: category.itemIds });
        } catch (e) {
            await api.delete(`/categories/${data.id}`).catch(() => undefined);
            throw e;
        }
        cb.categoryAttached(category.key, data.id);
    }

    for (const id of removed) {
        await api.delete(`/menus/${menuId}/categories/${id}`);
        cb.categoryDetached(id);
    }
    return menuId;
};
